import { TeamResponse } from '../types/team.types';
import { config } from '../config';
import { store } from '../store';
import { handleSessionExpired } from './sessionHandler';

const API_URL = config.API_URL;

export interface Team {
  id: string;
  name: string;
  description: string;
  created_by: string;
  status: string;
  created_at: string;
  updated_at: string;
  isOwner?: boolean;
  userRole?: string;
  userCount?: number;
  member_count?: number;
  role_id?: number;
  role_name?: string;
  storage_data?: any;
  team_type?: 'organization' | 'department';
  parent_team_id?: string;
  current_member_count?: number;
  member_limit?: number;
  license_type?: 'trial' | 'paid';
  trial_end_date?: string;
}

export interface TeamMember {
  id: string;
  user_id: string;
  email: string;
  role_id: number;
  role_name: string;
  added_by_email: string;
  team_role_id?: number;
  team_role_name?: string;
  created_at: string;
  two_factor_enabled: boolean;
  joined_at: string;
  team_name?: string;
  display_info?: string;
  dept_name?: string;
  dept_id?: string;
  team_id?: string;
}

export interface TeamInvitation {
  id: string;
  email: string;
  role_id: number;
  role_name: string;
  expires_at: string;
  created_at: string;
  invited_by_email: string;
}

export class TeamService {
    // Removed getToken; authentication now via session cookie

  static async getTeamMembers(teamId?: string): Promise<any> {
    const url = teamId ? `${API_URL}/teams/${teamId}/members` : `${API_URL}/team/members`;

    const response = await fetch(url, {
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include', // Send cookies for session auth
        });

      if (!response.ok) {
        if (response.status === 401) {
          handleSessionExpired();
        }
        throw new Error('Failed to fetch team members');
        }

        return response.json();
    }

    static async getActiveTeamMembers(teamId?: string): Promise<any> {
      const url = teamId ? `${API_URL}/teams/${teamId}/active-members` : `${API_URL}/team/active-members`;
  
      const response = await fetch(url, {
              headers: {
                  'Content-Type': 'application/json',
              },
              credentials: 'include', // Send cookies for session auth
          });
  
          if (!response.ok) {
              throw new Error('Failed to fetch team members');
          }
  
          return response.json();
      }

  static async inviteUser(email: string, roleId: number, teamId?: string, team_id?: string): Promise<any> {
    const body: any = { email, roleId, teamId, team_id };

    const response = await fetch(`${API_URL}/team/invite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
      credentials: 'include', // Send cookies for session auth
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to invite user');
    }
    
    return data;
  }

    static async revokeInvitation(invitationId: string): Promise<void> {
        const response = await fetch(`${API_URL}/team/invite/${invitationId}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include', // Send cookies for session auth
        });

        if (!response.ok) {
            throw new Error('Failed to revoke invitation');
        }
    }

    static async updateUserRole(userId: string, roleId: number, team_id?: string, isGuestUser?: boolean): Promise<any> {
        try {
            const response = await fetch(`${API_URL}/teams/update-role/${userId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ roleId, team_id, isGuestUser }),
                credentials: 'include', // Send cookies for session auth
            });

            const data = await response.json();
            return data;
        } catch (error) {
            throw new Error('Failed to update user role');
        }
    }

  static async removeMemberFromTeam(userId: string, teamId?: string, isGuestUser?: boolean): Promise<void> {
    const response = await fetch(`${API_URL}/teams/remove-member/${userId}?team_id=${teamId}&is_guest_user=${isGuestUser}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include', // Send cookies for session auth
        });

        if (!response.ok) {
            throw new Error('Failed to remove team member');
        }
    }

    static async removeTeamMember(memberId: string): Promise<any> {
        try {
            const response = await fetch(`${API_URL}/teams/remove-member/${memberId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include', // Send cookies for session auth
            });

            if (!response.ok) {
                throw new Error('Failed to remove team member');
            }

            const data = await response.json();
            return data;
        } catch (error) {
            throw new Error('Failed to remove team member');
        }
    }

  static async deleteUser(userId: string): Promise<void> {
    const response = await fetch(`${API_URL}/teams/delete-user/${userId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Send cookies for session auth
    });

    if (!response.ok) {
      throw new Error('Failed to remove team member');
    }
  }
    static async validateInvitationToken(token: string) {
        try {
          const response = await fetch(`${API_URL}/team/validate-invitation`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ token }),
          });
          
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to validate invitation');
          }
          
          return await response.json();
        } catch (error) {
          console.error('Error validating invitation token:', error);
          throw error;
        }
      }
    
  static async createTeam(name: string, description: string, encrypted_team_key: string, member?: any): Promise<any> {
    const response = await fetch(`${API_URL}/teams`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        name, 
        description, 
        encrypted_team_key,
        team_type: 'workspace',
        create_member: true,
        member: member
      }),
      credentials: 'include', // Send cookies for session auth
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create team');
    }

    return response.json();
  }

  static async getUserTeams(): Promise<{ teams: Team[] }> {
    const response = await fetch(`${API_URL}/teams`, {
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Send cookies for session auth
    });

    if (!response.ok) {
      throw new Error('Failed to fetch teams');
    }

    return response.json();
  }

  static async getTeamById(teamId: string): Promise<{ team: Team }> {
    const response = await fetch(`${API_URL}/teams/${teamId}`, {
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Send cookies for session auth
    });

    if (!response.ok) {
      throw new Error('Failed to fetch team details');
    }

    return response.json();
  }

  static async getTeamInvitations(teamId: string): Promise<{ invitations: TeamInvitation[] }> {
    const response = await fetch(`${API_URL}/teams/${teamId}/invitations`, {
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Send cookies for session auth
    });

    if (!response.ok) {
      throw new Error('Failed to fetch team invitations');
    }

    return response.json();
  }

  static async getUsersWithoutTeam(team_id?: string, dept_id?: string): Promise<{ users: any[] }> {
    const url = new URL(`${API_URL}/teams/users/available`);

    if (team_id !== undefined) {
      url.searchParams.append('team_id', team_id);
    }

    if (dept_id !== undefined) {
      url.searchParams.append('dept_id', dept_id);
    }

    const response = await fetch(url.toString(), {
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Send cookies for session auth
    });

    if (!response.ok) {
      throw new Error('Failed to fetch available users');
    }

    return response.json();
  }

  static async addMemberToTeam(userId: string, teamId: string, roleId: number): Promise<any> {
    const response = await fetch(`${API_URL}/teams/${teamId}/members`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId,
        role_id: roleId
      }),
      credentials: 'include', // Send cookies for session auth
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to add member to team');
    }

    return response.json();
  }

  static async addBulkMembersToTeam(users: { user_id: string; role_id: number }[], teamId: string): Promise<any> {
    const response = await fetch(`${API_URL}/teams/${teamId}/bulk-members`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        users,
        team_id: teamId
      }),
      credentials: 'include', // Send cookies for session auth
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to add members to team');
    }

    return response.json();
  }

  static async removeMember(teamId: string, memberId: string): Promise<any> {
    const response = await fetch(`${API_URL}/teams/${teamId}/members/${memberId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Send cookies for session auth
    });

    if (!response.ok) {
      throw new Error('Failed to remove team member');
    }

    return response.json();
  }

  static async deleteTeam(teamId: string, deleteUsers: boolean = false): Promise<any> {
    try {
        const response = await fetch(`${API_URL}/teams/${teamId}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ deleteUsers }),
            credentials: 'include', // Send cookies for session auth
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to delete team');
        }
        
        return await response.json();
    } catch (error) {
        console.error('Delete team error:', error);
        throw error;
    }
  }

  static async acceptInvitation(token: string): Promise<any> {
    try {
      const response = await fetch(`${API_URL}/team/accept-invitation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to accept invitation');
      }
      
      return data;
    } catch (error) {
      console.error('Accept invitation error:', error);
      throw error;
    }
  }

  static async updateTeam(teamId: string, teamData: { name: string; description: string }): Promise<any> {
    try {
        const response = await fetch(`${API_URL}/teams/${teamId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(teamData),
            credentials: 'include', // Send cookies for session auth
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to update team');
        }
        
        return await response.json();
    } catch (error) {
        console.error('Update team error:', error);
        throw error;
    }
  }

  static async fetchTeamStorageUsage(teamId: string): Promise<any> {
    const response = await fetch(`${API_URL}/teams/${teamId}/fetch-team-storage-usage`, {
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Send cookies for session auth
    });

    if (!response.ok) {
      throw new Error('Failed to fetch team storage usage');
    }

    return response.json();
  }

  static async getTeamStorageData(teamId: string): Promise<any> {
    const response = await fetch(`${API_URL}/teams/${teamId}/storage-data`, {
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Send cookies for session auth
    });

    if (!response.ok) {
      throw new Error('Failed to fetch team storage data');
    }

    return response.json();
  }


  static async inviteMember(teamId: string, email: string, roleId: number): Promise<any> {
    const response = await fetch(`${API_URL}/teams/${teamId}/invitations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        role_id: roleId
      }),
      credentials: 'include', // Send cookies for session auth
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to send invitation');
    }

    return response.json();
  }

  static async transferIdentities(memberId: string, teamId: string, actionType: string): Promise<any> {
    const response = await fetch(`${API_URL}/teams/transfer-identities`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            memberId,
            teamId,
            adminId: store.getState().auth.user?.id,
            actionType
        }),
        credentials: 'include', // Send cookies for session auth
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to transfer identities');
    }

    return response.json();
  }

  static async getLicenseStatus(teamId: string) {
    try {
      const response = await fetch(`${API_URL}/teams/${teamId}/license`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Send cookies for session auth
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching license status:', error);
      throw error;
    }
  }

  static async upgradeLicense(teamId: string, paymentDetails: any) {
    try {
      const response = await fetch(`${API_URL}/teams/${teamId}/upgrade`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paymentDetails),
        credentials: 'include', // Send cookies for session auth
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error upgrading license:', error);
      throw error;
    }
  }

  static async checkTrialEligibility(email: string) {
    try {
      const response = await fetch(`${API_URL}/teams/trial-eligibility`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error checking trial eligibility:', error);
      throw error;
    }
  }

  static async getTransferableItems(memberId: string, teamId?: string): Promise<any> {
    const url = teamId 
      ? `${API_URL}/teams/transferable-items/${memberId}/${teamId}` 
      : `${API_URL}/teams/transferable-items/${memberId}`;
      
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include', // Send cookies for session auth
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch transferable items');
    }

    return response.json();
  }

  static async transferSelectedItems(memberId: string, selectedItems: any[]): Promise<any> {
    const response = await fetch(`${API_URL}/teams/transfer-selected-items`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            memberId,
            selectedItems
        }),
        credentials: 'include', // Send cookies for session auth
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to transfer selected items');
    }

    return response.json();
  }
}


export default TeamService;
